import { useState } from 'react';
import { useQuery } from 'react-query';

import { getV2ApiCompanyIdTravelPropertiesSessionSessionIdResults } from 'src/client/spendesk-api';
import type {
  FullResponse,
  GetV2ApiCompanyIdTravelPropertiesSessionSessionIdResultsResponseOK,
  GetV2ApiCompanyIdTravelPropertiesSessionSessionIdResultsResponses,
} from 'src/client/spendesk-api-types';

import { useCompanyId } from '../../app/hooks/useCompanyId';

const POLLING_INTERVAL = 3000;

const isSuccessResponse = (
  data:
    | GetV2ApiCompanyIdTravelPropertiesSessionSessionIdResultsResponses
    | undefined,
): data is FullResponse<
  GetV2ApiCompanyIdTravelPropertiesSessionSessionIdResultsResponseOK,
  200
> => {
  return data?.statusCode === 200;
};

export const usePropertiesSessionResultsQuery = (searchId?: string) => {
  const companyId = useCompanyId();
  const [refetchCount, setRefetchCount] = useState(0);

  const query = useQuery(
    ['properties', 'session', searchId, 'results'],
    () =>
      getV2ApiCompanyIdTravelPropertiesSessionSessionIdResults({
        path: { companyId, sessionId: searchId ?? '' },
        query: {
          cursor: 0,
          pageSize: 100,
        },
      }),
    {
      enabled: Boolean(searchId),
      refetchInterval: (data) => {
        // stop polling when status is "complete" or "failed"
        if (isSuccessResponse(data)) {
          const status = data.body.data.status;
          if (status === 'completed' || status === 'failed') {
            return false;
          }
        }
        return POLLING_INTERVAL;
      },
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        if (isSuccessResponse(data) && data.body.data.status === 'pending') {
          setRefetchCount((previous) => previous + 1);
        }
      },
    },
  );

  const successResponse =
    query.isSuccess && isSuccessResponse(query.data) ? query.data : null;
  const responseData: GetV2ApiCompanyIdTravelPropertiesSessionSessionIdResultsResponseOK | null =
    successResponse ? successResponse.body : null;
  const isStatusComplete = responseData?.data.status === 'completed';
  const isStatusPending = responseData?.data.status === 'pending';
  const isStatusFailed = responseData?.data.status === 'failed';
  const isHttpError =
    query.isError || (query.isSuccess && !isSuccessResponse(query.data));
  const isLoading =
    query.isLoading || (successResponse !== null && isStatusPending);
  const results = isStatusComplete ? responseData.data.results : [];

  // Progress for display progress bar (cap at 95% until complete)
  const progress =
    isStatusComplete || isStatusFailed ? 100 : Math.min(95, refetchCount * 10);

  return {
    ...query,
    isLoading,
    data: responseData,
    results,
    isStatusComplete,
    isStatusPending,
    isStatusFailed,
    isHttpError,
    isError: isHttpError || isStatusFailed,
    progress,
  };
};
